import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { authAxios } from '../utils/api';
import { 
  Building2, Users, Trophy, ArrowRight, Search, UserPlus, 
  ChevronLeft, Mail, CheckCircle, Clock, XCircle, LogOut
} from 'lucide-react';

export default function CreateLeaguePage() {
  const { user, API, logout } = useContext(AppContext);
  const navigate = useNavigate();
  const [view, setView] = useState('choice'); // 'choice', 'create', 'join'
  const [loading, setLoading] = useState(false);
  
  // Create League State
  const [leagueName, setLeagueName] = useState('');
  
  // Join League State
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  
  // My Requests State
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  useEffect(() => {
    loadMyRequests();
  }, []);

  const loadMyRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await authAxios.get(`${API}/leagues/my-requests`);
      setMyRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    
    if (!leagueName.trim()) {
      toast.error('Please enter a league name');
      return;
    }

    try {
      setLoading(true);
      await authAxios.post(
        `${API}/leagues/create`,
        { name: leagueName.trim(), timezone: 'America/Denver' }
      );
      
      toast.success('League created successfully!');
      window.location.href = '/home';
    } catch (error) {
      console.error('Failed to create league:', error);
      toast.error(error.response?.data?.detail || 'Failed to create league');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchEmail.trim() || searchEmail.length < 3) {
      toast.error('Please enter at least 3 characters to search');
      return;
    }

    try {
      setSearching(true);
      const response = await authAxios.get(
        `${API}/leagues/search?email=${encodeURIComponent(searchEmail)}`
      );
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        toast.info('No leagues found with that admin email');
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleRequestJoin = async () => {
    if (!selectedLeague) return;

    try {
      setLoading(true);
      await authAxios.post(
        `${API}/leagues/${selectedLeague.tenant_id}/request-join`,
        { message: joinMessage }
      );
      
      toast.success('Join request sent! The League Admin will review your request.');
      setShowJoinDialog(false);
      setSelectedLeague(null);
      setJoinMessage('');
      loadMyRequests();
      setView('choice');
    } catch (error) {
      console.error('Request failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await authAxios.delete(`${API}/leagues/requests/${requestId}`);
      toast.success('Request cancelled');
      loadMyRequests();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-600"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'denied':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Pending requests view
  const pendingRequests = myRequests.filter(r => r.status === 'pending');

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ 
      background: 'linear-gradient(135deg, #1e3a5f 0%, #2c5f7f 50%, #1a4d6b 100%)'
    }}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M30 0L45 15L30 30L15 15L30 0zM0 30L15 45L0 60L0 30zM60 30L60 60L45 45L60 30z'/%3E%3C/g%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px'
      }}></div>

      {/* Sign Out Button */}
      <div className="absolute top-4 right-4 z-20">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={logout}
          className="bg-white/10 border-white/30 text-white hover:bg-white/20"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-16 relative z-10">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 bg-amber-500 rounded-full flex items-center justify-center">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Welcome, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-amber-200">Join a league or start your own</p>
          </div>

          {/* Pending Requests Banner */}
          {pendingRequests.length > 0 && view === 'choice' && (
            <Card className="mb-6 bg-amber-50 border-amber-300">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-900">
                      You have {pendingRequests.length} pending join request{pendingRequests.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-amber-700">
                      Waiting for approval from: {pendingRequests.map(r => r.league_name).join(', ')}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleCancelRequest(pendingRequests[0].id)}
                    className="text-amber-700 border-amber-400"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Choice View */}
          {view === 'choice' && (
            <div className="grid gap-4">
              {/* Join a League Option */}
              <Card 
                className="border-2 border-transparent hover:border-emerald-500 cursor-pointer transition-all"
                onClick={() => setView('join')}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                      <UserPlus className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-800">Join an Existing League</h3>
                      <p className="text-sm text-slate-600">Search by the League Admin's email address</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>

              {/* Create a League Option */}
              <Card 
                className="border-2 border-transparent hover:border-amber-500 cursor-pointer transition-all"
                onClick={() => setView('create')}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-800">Start Your Own League</h3>
                      <p className="text-sm text-slate-600">Create a new league and become the admin</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Create League View */}
          {view === 'create' && (
            <Card className="border-2 border-amber-600">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setView('choice')} className="mr-2">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <CardTitle>Create Your League</CardTitle>
                    <CardDescription>You'll be the League Admin with full control</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateLeague} className="space-y-6">
                  <div>
                    <Label htmlFor="league-name">League Name</Label>
                    <Input
                      id="league-name"
                      type="text"
                      placeholder="e.g., Bishop Family Survivor League"
                      value={leagueName}
                      onChange={(e) => setLeagueName(e.target.value)}
                      className="mt-2"
                      autoFocus
                    />
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                    <p className="font-medium text-slate-700">As League Admin, you can:</p>
                    <ul className="space-y-1 text-slate-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Invite up to 20 players per season
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Create seasons and manage scoring rules
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Run drafts and track scores
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        Approve or deny join requests
                      </li>
                    </ul>
                  </div>

                  <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading}>
                    {loading ? 'Creating...' : 'Create League'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Join League View */}
          {view === 'join' && (
            <Card className="border-2 border-emerald-600">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setView('choice')} className="mr-2">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div>
                    <CardTitle>Find a League to Join</CardTitle>
                    <CardDescription>Search by the League Admin's email address</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>League Admin Email</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="admin@example.com"
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      />
                    </div>
                    <Button onClick={handleSearch} disabled={searching}>
                      {searching ? 'Searching...' : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Ask your league admin for their email address
                  </p>
                </div>

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="space-y-3">
                    <Label>Results</Label>
                    {searchResults.map((league) => (
                      <div 
                        key={league.tenant_id}
                        className="border rounded-lg p-4 hover:border-emerald-500 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedLeague(league);
                          setShowJoinDialog(true);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-800">{league.league_name}</h4>
                            <p className="text-sm text-slate-600">
                              Admin: {league.admin_name} ({league.admin_email})
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              <Users className="w-3 h-3 inline mr-1" />
                              {league.member_count}/{league.max_players} members
                            </p>
                          </div>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                            Request to Join
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.length === 0 && searchEmail && !searching && (
                  <div className="text-center py-8 text-slate-500">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No leagues found</p>
                    <p className="text-sm">Make sure you have the correct email address</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Join Request Dialog */}
      <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request to Join {selectedLeague?.league_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-600">
                <strong>Admin:</strong> {selectedLeague?.admin_name} ({selectedLeague?.admin_email})
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <strong>Members:</strong> {selectedLeague?.member_count}/{selectedLeague?.max_players}
              </p>
            </div>
            
            <div>
              <Label>Message to Admin (optional)</Label>
              <Textarea
                placeholder="Introduce yourself or let them know how you found the league..."
                value={joinMessage}
                onChange={(e) => setJoinMessage(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </div>

            <p className="text-sm text-slate-500">
              The League Admin will review your request and decide whether to approve it.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleRequestJoin}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
